import moment from 'moment';
import SailBoat from 'assets/svgs/timeoff-sailboat.svg';
import { humanise } from 'components/atoms/humanised-date';
import { useTranslate } from 'hooks/use-translation';
import { UserAllocationSummaryEvent } from '../../../event-types';
import { AllocatedMinutes } from '../../../types';
import {
  getDetailedAllocationData,
  maximumDate,
  minimumDate,
} from '../../../utils/common';
import { ignoreMinutesIfZero } from '../../../utils/dates';
import { ViewPresetId } from '../../view-presets/constants';
import { userSummaryTemplateForDayView } from './day-view';
import { userSummaryTemplateForMonthAndWeekView } from './month-week-view';

interface Props
  extends Pick<
    UserAllocationSummaryEvent,
    'allocatedMinutes' | 'id' | 'timeOff' | 'forViewPreset'
  > {
  capacity: number;
  // ignoreSoftAllocations: boolean;
}

const getText = (
  allocatedMinutes: AllocatedMinutes,
  capacity: number,
  forViewPreset: ViewPresetId,
): string => {
  const { totalAllocatedMinutes, allocatedPercentage } =
    getDetailedAllocationData(allocatedMinutes, capacity);

  const allocatedHours = ignoreMinutesIfZero(totalAllocatedMinutes);

  return `${
    capacity === 0 ? 'OOO' : `${allocatedPercentage}%`
  } (${allocatedHours}${
    forViewPreset === ViewPresetId.Day ||
    forViewPreset === ViewPresetId.DayZoomed
      ? '/d'
      : ''
  })`;
};

export const userSummaryTemplate = ({
  allocatedMinutes,
  capacity,
  id,
  forViewPreset,
  timeOff,
}: Props): string => {
  let content = `<div class='content' data-event-id=${id}>${getText(
    allocatedMinutes,
    capacity,
    forViewPreset,
  )}</div>`;

  if (timeOff && timeOff.length > 0) {
    if (![ViewPresetId.Day, ViewPresetId.DayZoomed].includes(forViewPreset)) {
      content = content.concat(
        `<div class='time-off-icon' data-event-id=${id}><img src=${SailBoat} /></div>`,
      );
    }
  }

  return content;
};

type TooltipProps = Pick<
  UserAllocationSummaryEvent,
  'allocatedMinutes' | 'startDate' | 'endDate' | 'forViewPreset' | 'capacity'
> & {
  name?: string;
  softAllocationsEnabled?: boolean;
};
export const userSummaryTooltip = (
  {
    allocatedMinutes,
    name,
    startDate,
    endDate,
    capacity,
    forViewPreset,
    softAllocationsEnabled,
  }: TooltipProps,
  translate: ReturnType<typeof useTranslate>,
): string => {
  if (
    forViewPreset !== ViewPresetId.Day &&
    forViewPreset !== ViewPresetId.DayZoomed
  ) {
    return userSummaryTemplateForMonthAndWeekView({
      translate,
      softAllocationsEnabled,
      name,
      capacity,
      allocatedMinutes,
    });
  }

  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);
  const isStartDateSameAsMinimum = startDateMoment.isSame(minimumDate, 'week');
  const isEndDateSameAsMaximum = endDateMoment.isSame(maximumDate, 'week');
  const isSingleDay = startDateMoment.isSame(endDateMoment, 'day');
  const startDateString = humanise(startDateMoment);
  const endDateString = humanise(endDateMoment);

  const { allocatedPercentage, availableMinutes } = getDetailedAllocationData(
    allocatedMinutes,
    capacity,
  );

  const key =
    allocatedPercentage > 100
      ? 'over'
      : allocatedPercentage === 100
      ? 'full'
      : allocatedPercentage < 100 && allocatedPercentage > 0
      ? 'partially_free'
      : 'free';

  const hasDates =
    isStartDateSameAsMinimum && isEndDateSameAsMaximum // TODO: to check the handling of date ranges
      ? 'noDates'
      : isStartDateSameAsMinimum
      ? 'onlyEndDate'
      : isEndDateSameAsMaximum
      ? 'onlyStartDate'
      : isSingleDay
      ? 'singleDay'
      : false;

  return userSummaryTemplateForDayView({
    translate,
    key,
    name,
    startDateString,
    endDateString,
    hasDates,
    availableMinutes,
  });
};
